import { all, call, delay, put, select, takeLatest } from 'redux-saga/effects';
import { customAlphabet } from 'nanoid';

import { checkHiddenByRole } from 'src/functions';
import { RT_API_LIST } from 'src/store/ActionTypes';
import * as api from 'src/store/api';
import * as actions from 'src/store/actions/rt_api_list';
import CONSTANTS from 'src/store/constants';

const nanoid = customAlphabet('1234567890abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ', 8);
const selectGridValues = ({
  rtApiList: { selectedConfigIds, gridState, controlledGridState, selectedGridRows, maxGridRowSelection },
}) => ({
  selectedConfigIds,
  gridState,
  controlledGridState,
  selectedGridRows,
  maxGridRowSelection,
});

/**
 * approveConfig - flow control for approving a realtime config
 * @param {{type: string, payload: string }} action
 * @returns {Generator<*, void, *>}
 */
function* approveConfig(action) {
  try {
    const { payload: configId } = action;
    yield call(api.createRtConfigStatus, configId, { status: CONSTANTS.REAL_TIME_API.CS_STATE.APPROVED });
    yield put({
      type: RT_API_LIST.APPROVE_CONFIG_SUCCEEDED,
    });
  } catch (err) {
    yield put({
      type: RT_API_LIST.APPROVE_CONFIG_FAILURE,
      payload: err.message,
    });
  } finally {
    yield delay(500);
    yield put(actions.unset('approveConfigError'));
  }
}

/**
 * editConfig - flow control for creating a realtime config
 * @param {{type: string, payload: import('../typings.js').RtCs }} action
 * @returns {Generator<*, void, *>}
 */
function* editConfig(action) {
  try {
    const {
      payload: { id: configId, update },
    } = action;
    yield call(api.editRtConfig, configId, update);
    yield put({
      type: RT_API_LIST.EDIT_CONFIG_SUCCEEDED,
    });
  } catch (err) {
    yield put({
      type: RT_API_LIST.EDIT_CONFIG_FAILURE,
      payload: err.message,
    });
  } finally {
    yield delay(500);
    yield put(actions.unset('editConfigError'));
  }
}

/**
 * createConfig - flow control for creating a realtime config
 * @param {{type: string, payload: import('../typings.js').RtCs }} action
 * @returns {Generator<*, void, *>}
 */
function* createConfig(action) {
  try {
    // NOTE: Check if the config has been set from the list page, and matches the requested config
    const { payload: config } = action;
    config.id = nanoid();
    yield call(api.createRtConfig, config);
    yield put({
      type: RT_API_LIST.CREATE_CONFIG_SUCCEEDED,
    });
  } catch (err) {
    yield put({
      type: RT_API_LIST.CREATE_CONFIG_FAILURE,
      payload: err.message,
    });
  } finally {
    yield delay(500);
    yield put(actions.unset('createConfigError'));
  }
}

/**
 * deleteConfig - flow control for approving a realtime config
 * @param {{type: string, payload: string }} action
 * @returns {Generator<*, void, *>}
 */
function* deleteConfig(action) {
  try {
    const { payload: configId } = action;
    yield call(api.deleteRtConfig, configId);
    yield put({
      type: RT_API_LIST.DELETE_CONFIG_SUCCEEDED,
    });
  } catch (err) {
    yield put({
      type: RT_API_LIST.DELETE_CONFIG_FAILURE,
      payload: err.message,
    });
  } finally {
    yield delay(500);
    yield put(actions.unset('deleteConfigError'));
  }
}

/**
 * fetchConfigList - flow control for fetching a list of configs
 * @param {{type: string, payload: object }} action
 * @returns {Generator<*, void, *>}
 */
function* fetchConfigList(action) {
  try {
    const {
      payload: { includeRefresh, ...query },
    } = action;
    const configList = yield call(api.fetchRtConfigs, query);
    const modelList = configList.map((cs, idx) => {
      if (cs.score_type === 'static')
        return {
          name: 'Test Environment',
          typeDetail: 'TEST',
          refresh: {
            platform: CONSTANTS.PLATFORM.REFRESH_VERSIONS.UPGRADED,
          },
          modelData: {
            metadataPayload: {
              refresh: {
                platform: CONSTANTS.PLATFORM.REFRESH_VERSIONS.UPGRADED,
              },
            },
          },
        };
      if (!cs.config_metadata.model_id) {
        return {
          id: -1 * Math.random(),
          name: cs.config_metadata.name || 'CUSTOM MODEL',
          typeDetail: cs.config_metadata.type_detail || 'CUSTOM TYPE',
          dataModelType: cs.config_metadata.data_model_type || 'CLASSIFIER',
        };
      }
      const options = {
        headers: {
          'X-Fields': 'id,name,dataModelType,framework,modelData,predictionType,typeDetail',
        },
      };
      return call(api.fetchModel, cs.config_metadata.model_id, options);
    });
    const models = yield all(modelList);
    models.forEach((model, idx) => {
      configList[idx].config_metadata.model = model;
    });

    if (includeRefresh) {
      const segmentTypeMap = CONSTANTS.TAL_MODEL.SEGMENT_TYPE;
      const segmentMap = {};
      // Retrieve segments referenced in the modelData field
      for (const model of models) {
        const dataPayload = model?.modelData?.dataPayload ?? null;
        const id = model?.id ?? null;
        if (dataPayload) {
          Object.entries(dataPayload).forEach(([key, value]) => {
            if (key.includes('segment_id') && value) {
              if (!segmentMap[value]) segmentMap[value] = [segmentTypeMap[key], [id]];
              else {
                segmentMap[value][1].push(id);
              }
            }
          });
        }
      }
      const segmentIds = Object.keys(segmentMap);
      const segmentOptions = {
        headers: {
          'X-Fields': 'items{id,name,refresh{id,name,platform}}',
        },
      };
      const segmentQuery = {
        filters: [
          {
            name: 'id',
            op: 'in',
            val: segmentIds,
          },
        ],
      };
      const segments = yield call(api.fetchSegmentsExperimental, segmentQuery, segmentOptions);
      // walk the results and set segment refresh
      segments.forEach((segmentData) => {
        const { id: segmentId } = segmentData;
        const [, modelIds] = segmentMap[segmentId];
        modelIds.forEach((modelId) => {
          configList
            .filter((config) => config?.config_metadata?.model_id === modelId)
            .forEach((config) => {
              const configIdx = configList.findIndex((c) => c.id === config.id);
              configList[configIdx].config_metadata.model.refresh = segmentData.refresh;
            });
        });
      });
    }

    /** @param {import('../typings.js').RtCs} cs */
    yield put({
      type: RT_API_LIST.FETCH_CONFIG_LIST_SUCCEEDED,
      payload: configList,
    });
  } catch (e) {
    yield put({
      type: RT_API_LIST.FETCH_CONFIG_LIST_FAILURE,
      payload: e.message,
    });
  } finally {
    yield delay(500);
    yield put(actions.unset('fetchConfigListError'));
  }
}

function* selectGridRow(action) {
  const { payload: row } = action;
  yield put({
    type: RT_API_LIST.SET_SELECTED_ROWS,
    payload: [row],
  });
}

function* deselectGridRow(action) {
  const { payload: row } = action;
  const { selectedGridRows } = yield select(selectGridValues);
  const filteredSelectedGridRows = selectedGridRows.filter(({ id }) => id !== row.id);
  yield put({
    type: RT_API_LIST.SET_SELECTED_ROWS,
    payload: filteredSelectedGridRows,
  });
}

/**
 * fetchApiKey - flow control for fetching an RT API key
 * @return {Generator<*, void, *>}
 */
function* fetchApiKey(action) {
  try {
    const response = yield call(api.fetchRealTimeApiKey);
    yield put({
      type: RT_API_LIST.FETCH_API_KEY_SUCCEEDED,
      payload: response.apiKey,
    });
  } catch (err) {
    yield put({
      type: RT_API_LIST.FETCH_API_KEY_FAILURE,
      payload: err.message,
    });
  } finally {
    yield delay(500);
    yield put(actions.unset('fetchApiKeyError'));
  }
}

function* fetchModels(action) {
  try {
    const {
      payload: { fields = '*', includeRefresh, ...query },
    } = action;
    const options = {
      headers: {
        'X-Fields': fields,
      },
    };
    let models = yield call(api.fetchModels, query, options);
    const siteRole = yield select((state) => state?.app?.profile?.siteRole ?? 'CLIENT_USER');
    const role = yield select((state) => state?.app?.profile?.role ?? 'VIEWER');
    models = models.filter((mdl) => {
      const roleCheck = !checkHiddenByRole(role, mdl?.hiddenByRole);
      const siteRoleCheck = !checkHiddenByRole(siteRole, mdl?.hiddenByRole);
      return siteRoleCheck || roleCheck;
    });

    if (includeRefresh) {
      const segmentTypeMap = CONSTANTS.TAL_MODEL.SEGMENT_TYPE;
      const segmentMap = {};
      // Retrieve segments referenced in the modelData field
      for (const model of models) {
        const dataPayload = model?.modelData?.dataPayload ?? null;
        const id = model?.id ?? null;
        if (dataPayload) {
          Object.entries(dataPayload).forEach(([key, value]) => {
            if (key.includes('segment_id') && value) {
              if (!segmentMap[value]) segmentMap[value] = [segmentTypeMap[key], [id]];
              else {
                segmentMap[value][1].push(id);
              }
            }
          });
        }
      }
      const segmentIds = Object.keys(segmentMap);
      const segmentOptions = {
        headers: {
          'X-Fields': 'items{id,name,refresh{id,name,platform}}',
        },
      };
      const segmentQuery = {
        filters: [
          {
            name: 'id',
            op: 'in',
            val: segmentIds,
          },
        ],
      };
      const segments = yield call(api.fetchSegmentsExperimental, segmentQuery, segmentOptions);
      // walk the results and set segment refresh
      segments.forEach((segmentData) => {
        const { id: segmentId } = segmentData;
        const [, modelIds] = segmentMap[segmentId];
        modelIds.forEach((modelId) => {
          const modelIdx = models.findIndex((model) => +model.id === +modelId);
          models[modelIdx].refresh = segmentData.refresh;
        });
      });
    }

    yield put({
      type: RT_API_LIST.FETCH_MODELS_SUCCEEDED,
      payload: models,
    });
  } catch (e) {
    yield put({
      type: RT_API_LIST.FETCH_MODELS_FAILED,
    });
  } finally {
    yield delay(500);
    yield put({
      type: RT_API_LIST.UNSET,
      payload: 'fetchModelStatus',
    });
  }
}

function* onRowSelectSaga() {
  yield takeLatest(RT_API_LIST.SELECT_ROW, selectGridRow);
}

function* onRowDeselectSaga() {
  yield takeLatest(RT_API_LIST.DESELECT_ROW, deselectGridRow);
}

function* approveConfigSaga() {
  yield takeLatest(RT_API_LIST.APPROVE_CONFIG, approveConfig);
}

function* createConfigSaga() {
  yield takeLatest(RT_API_LIST.CREATE_CONFIG, createConfig);
}

function* deleteConfigSaga() {
  yield takeLatest(RT_API_LIST.DELETE_CONFIG, deleteConfig);
}

function* editConfigSaga() {
  yield takeLatest(RT_API_LIST.EDIT_CONFIG, editConfig);
}

function* fetchConfigListSaga() {
  yield takeLatest(RT_API_LIST.FETCH_CONFIG_LIST, fetchConfigList);
}

function* fetchApiKeySaga() {
  yield takeLatest(RT_API_LIST.FETCH_API_KEY, fetchApiKey);
}

function* fetchModelsSaga() {
  yield takeLatest(RT_API_LIST.FETCH_MODELS, fetchModels);
}

export default [
  fetchApiKeySaga(),
  fetchConfigListSaga(),
  approveConfigSaga(),
  createConfigSaga(),
  deleteConfigSaga(),
  editConfigSaga(),
  onRowDeselectSaga(),
  onRowSelectSaga(),
  fetchModelsSaga(),
];
